import { useMemo } from 'react';

import { demoWorkloadsGpuUtilizationSummary } from '@cast/fixtures';

import { useDebounce } from '@cast/utils';
import { useCostReportContext } from 'features/cost-report/hooks/useCostReportContext';
import {
  useWorkloadsGpuUtilizationSummaryQuery,
  UseWorkloadsGpuUtilizationSummaryQueryProps,
} from 'hooks/queries/cost';
import { useParams } from 'hooks/useParams';
import { UseServerData } from '@cast/shared-search-ui';

import { useGpuUtilizationDataOrFixture } from '../../_hooks/useGpuUtilizationDataOrFixture';
import { normalizeGpuUtilizationSummary } from '../_utils/_utils';

export const useGpuUtilizationSummaryData: UseServerData = ({
  values,
  sortingState,
}) => {
  const { clusterId } = useParams<{ clusterId: string }>();
  const { labels, namespaces, workloadNames, workloadTypes } =
    useDebounce(values);
  const { hasGpuMetrics, isCapabilitiesLoading, adjustedDateRange } =
    useCostReportContext();

  const [from, to] = adjustedDateRange;

  const queryFilter: UseWorkloadsGpuUtilizationSummaryQueryProps = {
    enabled: hasGpuMetrics,
    clusterId,
    startTime: from.toISOString(),
    endTime: to.toISOString(),
    labels,
    namespaces,
    workloadNames,
    workloadTypes,
    sortField: sortingState?.columnId ?? 'wasted',
    sortOrder: sortingState?.sortBy?.direction ?? 'desc',
  };

  const {
    isLoading,
    data: _data,
    count,
    error,
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
  } = useWorkloadsGpuUtilizationSummaryQuery(queryFilter);

  const summaryDataFixture = useMemo(
    () => () =>
      demoWorkloadsGpuUtilizationSummary().then((data) => data.workloads),
    []
  );

  const data = useGpuUtilizationDataOrFixture(_data, summaryDataFixture);

  const normalizedGpuUtilizationSummary = useMemo(() => {
    return normalizeGpuUtilizationSummary(data ?? []);
  }, [data]);

  return {
    data: normalizedGpuUtilizationSummary,
    isLoading: isLoading || isCapabilitiesLoading,
    error,
    refetch,
    metadata: count || data.length,
    isFetching,
    hasNextPage: !isLoading && hasNextPage,
    fetchNextPage,
    hasPreviousPage: !isLoading,
  };
};
